import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CalendarDays, MessageCircle, Database, Search, CheckCircle, Loader2, X, ExternalLink, Eye, EyeOff, Link } from 'lucide-react';
import SettingsLayout from './SettingsLayout';
import { notifyBellChanged } from '../../lib/bellLinkage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsIntegrationsPage() {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Gmail state
    const [gmailStatus, setGmailStatus] = useState(null);
    const [gmailLoading, setGmailLoading] = useState(false);
    const [connectingGmail, setConnectingGmail] = useState(false);

    const [outlookStatus, setOutlookStatus] = useState(null);
    const [outlookLoading, setOutlookLoading] = useState(false);
    const [connectingOutlook, setConnectingOutlook] = useState(false);

    // Mail this provider left in the personal inbox while showing as
    // disconnected. Disconnect is the only control that hard-deletes synced
    // mail, and it renders only while status says *connected* — so any state
    // that leaves rows behind while status reads disconnected would strand
    // them with no way to clear them.
    //
    // The original cause of that state is gone: an admin revoking the mailbox
    // from the unified inbox no longer touches the personal credential, so it
    // can no longer flip this page's status. What remains is the residue — a
    // purge that failed partway, or rows predating the credential split — so
    // this stays as a recovery path rather than a workaround. Counted from the
    // personal inbox itself, so it appears only when something is genuinely
    // stranded.
    const [gmailOrphans, setGmailOrphans] = useState(0);
    const [outlookOrphans, setOutlookOrphans] = useState(0);
    const [clearingOrphans, setClearingOrphans] = useState(null);

    // Calendar state
    const [calendarStatus, setCalendarStatus] = useState(null);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [connectingCalendar, setConnectingCalendar] = useState(false);

    // Apify state
    const [apifyKey, setApifyKey] = useState('');
    const [apifyStatus, setApifyStatus] = useState(null);
    const [apifyValidating, setApifyValidating] = useState(false);
    const [showApifyKey, setShowApifyKey] = useState(false);

    // WhatsApp state
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [connectingWhatsApp, setConnectingWhatsApp] = useState(false);

    // Exa state
    const [exaKey, setExaKey] = useState('');
    const [exaStatus, setExaStatus] = useState(null);
    const [exaValidating, setExaValidating] = useState(false);
    const [showExaKey, setShowExaKey] = useState(false);

    // Check for OAuth callback params
    useEffect(() => {
        if (searchParams.get('gmail_connected') === 'true') {
            toast.success('Gmail connected successfully!');
            loadGmailStatus();
            // F08: dispatch event so React Query consumers (Inbox) invalidate.
            window.dispatchEvent(new CustomEvent('gmail-connected'));
            window.history.replaceState({}, '', '/settings/integrations');
        }
        if (searchParams.get('gmail_error')) {
            toast.error(`Gmail connection failed: ${searchParams.get('gmail_error')}`);
            window.history.replaceState({}, '', '/settings/integrations');
        }
        if (searchParams.get('outlook_connected') === 'true') {
            toast.success('Outlook connected successfully!');
            loadOutlookStatus();
            // Dispatch event so React Query consumers invalidate
            window.dispatchEvent(new CustomEvent('outlook-connected'));
            window.history.replaceState({}, '', '/settings/integrations');
        }
        if (searchParams.get('outlook_error')) {
            toast.error(`Outlook connection failed: ${searchParams.get('outlook_error')}`);
            window.history.replaceState({}, '', '/settings/integrations');
        }
        if (searchParams.get('calendar_connected') === 'true') {
            toast.success('Google Calendar connected successfully!');
            loadCalendarStatus();
            window.history.replaceState({}, '', '/settings/integrations');
        }
        if (searchParams.get('calendar_error')) {
            toast.error(`Calendar connection failed: ${searchParams.get('calendar_error')}`);
            window.history.replaceState({}, '', '/settings/integrations');
        }
        // Legacy connect/error query params from the retired video-conferencing
        // integration are ignored (#513); stale bookmarks simply no-op.
    }, [searchParams]);

    // Load integration statuses on mount
    useEffect(() => {
        loadGmailStatus();
        loadOutlookStatus();
        loadCalendarStatus();
        loadWhatsAppStatus();
        loadApifyStatus();
        loadExaStatus();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** How many personal-inbox rows this provider still holds. */
    const countPersonalMail = async (provider) => {
        try {
            const res = await axios.get(`${API}/inbox/personal/emails`, {
                headers: getAuthHeader(),
                params: { provider, page: 1, page_size: 1 },
            });
            return res.data?.data?.total || 0;
        } catch {
            // A count is a convenience, never a gate — failing to read it just
            // means no escape hatch is offered this render.
            return 0;
        }
    };

    const loadGmailStatus = async () => {
        setGmailLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/gmail/status`, { headers: getAuthHeader() });
            setGmailStatus(res.data);
            setGmailOrphans(res.data?.connected ? 0 : await countPersonalMail('gmail'));
        } catch (err) {
            console.error('Failed to load Gmail status:', err);
        } finally {
            setGmailLoading(false);
        }
    };

    const loadOutlookStatus = async () => {
        setOutlookLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/outlook/status`, { headers: getAuthHeader() });
            setOutlookStatus(res.data);
            setOutlookOrphans(res.data?.connected ? 0 : await countPersonalMail('outlook'));
        } catch (err) {
            console.error('Failed to load Outlook status:', err);
        } finally {
            setOutlookLoading(false);
        }
    };

    /**
     * Clear mail a disconnected provider left behind.
     *
     * Deliberately the SAME endpoint as Disconnect rather than a new one: the
     * disconnect handlers are idempotent and purge whether or not a live token
     * is found, so "disconnect again" is exactly the right verb for a mailbox
     * that already reads as disconnected.
     */
    const clearOrphanedMail = async (provider) => {
        setClearingOrphans(provider);
        try {
            const path = provider === 'gmail' ? 'gmail' : 'outlook';
            const res = await axios.post(
                `${API}/oauth/${path}/disconnect`, {}, { headers: getAuthHeader() }
            );
            const purged = res.data?.purged_emails ?? res.data?.data?.purged_emails ?? 0;
            toast.success(
                purged
                    ? `Removed ${purged} email${purged === 1 ? '' : 's'} from your inbox`
                    : 'Nothing left to remove'
            );
            window.dispatchEvent(new CustomEvent(`${provider}-disconnected`));
            notifyBellChanged(); // #803 — the purge removed those emails' bell entries
            if (provider === 'gmail') {
                setGmailOrphans(0);
                loadGmailStatus();
            } else {
                setOutlookOrphans(0);
                loadOutlookStatus();
            }
        } catch {
            toast.error('Failed to remove the leftover email');
        } finally {
            setClearingOrphans(null);
        }
    };

    const connectGmail = async () => {
        setConnectingGmail(true);
        try {
            const res = await axios.post(`${API}/oauth/google/start`, { target: 'personal' }, { headers: getAuthHeader() });
            const authUrl = res.data?.data?.auth_url || res.data?.auth_url;
            if (authUrl) {
                window.location.href = authUrl;
            } else {
                toast.error('No authorization URL returned');
                setConnectingGmail(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start Gmail connection');
            setConnectingGmail(false);
        }
    };

    const connectOutlook = async () => {
        setConnectingOutlook(true);
        try {
            const res = await axios.post(`${API}/oauth/outlook/connect`, {}, { headers: getAuthHeader() });
            const authUrl = res.data?.data?.auth_url || res.data?.auth_url;
            if (authUrl) {
                window.location.href = authUrl;
            } else {
                toast.error('No authorization URL returned');
                setConnectingOutlook(false);
            }
        } catch (err) {
            toast.error('Failed to start Outlook connection');
            setConnectingOutlook(false);
        }
    };

    const disconnectGmail = async () => {
        try {
            await axios.post(`${API}/oauth/gmail/disconnect`, {}, { headers: getAuthHeader() });
            setGmailStatus({ connected: false });
            setGmailOrphans(0);
            toast.success('Gmail disconnected');
            // F08: dispatch event so React Query consumers (Inbox) invalidate
            // their cached gmail status/emails immediately instead of showing
            // stale data for up to 5 minutes.
            window.dispatchEvent(new CustomEvent('gmail-disconnected'));
            notifyBellChanged(); // #803 — the purge removed those emails' bell entries
        } catch {
            toast.error('Failed to disconnect Gmail');
        }
    };

    const disconnectOutlook = async () => {
        try {
            await axios.post(`${API}/oauth/outlook/disconnect`, {}, { headers: getAuthHeader() });
            setOutlookStatus({ connected: false });
            setOutlookOrphans(0);
            toast.success('Outlook disconnected');
            // Dispatch event so React Query consumers invalidate
            window.dispatchEvent(new CustomEvent('outlook-disconnected'));
            notifyBellChanged(); // #803 — the purge removed those emails' bell entries
        } catch {
            toast.error('Failed to disconnect Outlook');
        }
    };

    // ── WhatsApp functions ─────────────────────────────────────────────────
    const loadWhatsAppStatus = async () => {
        setWhatsappLoading(true);
        try {
            const res = await axios.get(`${API}/whatsapp/status`, { headers: getAuthHeader() });
            setWhatsappStatus(res.data);
        } catch (err) {
            console.error('Failed to load WhatsApp status:', err);
        } finally {
            setWhatsappLoading(false);
        }
    };

    const connectWhatsApp = async () => {
        setConnectingWhatsApp(true);
        try {
            // Load Facebook SDK dynamically
            if (!window.FB) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://connect.facebook.net/en_US/sdk.js';
                    script.async = true;
                    script.defer = true;
                    script.onload = resolve;
                    script.onerror = reject;
                    document.body.appendChild(script);
                });
                window.fbAsyncInit = () => {
                    window.FB.init({
                        appId: process.env.REACT_APP_META_APP_ID,
                        autoLogAppEvents: true,
                        xfbml: false,
                        version: 'v21.0',
                    });
                };
                window.fbAsyncInit();
            }

            // Launch Embedded Signup
            window.FB.login((response) => {
                if (response.authResponse) {
                    const code = response.authResponse.code;
                    // Complete signup on backend
                    _completeWhatsAppSignup(code);
                } else {
                    toast.error('WhatsApp connection cancelled');
                    setConnectingWhatsApp(false);
                }
            }, {
                config_id: process.env.REACT_APP_META_CONFIG_ID, // Embedded Signup config
                response_type: 'code',
                override_default_response_type: true,
                extras: {
                    setup: {}, // WhatsApp Embedded Signup
                    featureType: '',
                    sessionInfoVersion: 2,
                },
            });
        } catch (err) {
            toast.error('Failed to start WhatsApp connection');
            setConnectingWhatsApp(false);
        }
    };

    const _completeWhatsAppSignup = async (code) => {
        try {
            // The Embedded Signup callback provides code; we also need waba_id and phone_number_id
            // These come from the sessionInfoListener message event
            const res = await axios.post(`${API}/whatsapp/connect`, {
                code,
                waba_id: window._whatsappWabaId || '',
                phone_number_id: window._whatsappPhoneNumberId || '',
                display_name: window._whatsappDisplayName || '',
                phone_number: window._whatsappPhoneNumber || '',
            }, { headers: getAuthHeader() });
            toast.success(`WhatsApp connected: ${res.data.display_name || 'Success'}`);
            loadWhatsAppStatus();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to complete WhatsApp setup';
            toast.error(typeof detail === 'string' ? detail : detail.message || 'Failed');
        } finally {
            setConnectingWhatsApp(false);
        }
    };

    // Listen for Embedded Signup session info (WABA ID, phone number ID)
    useEffect(() => {
        const handler = (event) => {
            if (event.data?.type === 'WA_EMBEDDED_SIGNUP') {
                const data = event.data.data;
                if (data) {
                    window._whatsappWabaId = data.waba_id || '';
                    window._whatsappPhoneNumberId = data.phone_number_id || '';
                    window._whatsappDisplayName = data.display_name || '';
                    window._whatsappPhoneNumber = data.phone_number || '';
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const disconnectWhatsApp = async () => {
        try {
            await axios.post(`${API}/whatsapp/disconnect`, {
                account_id: whatsappStatus?.account_id,
            }, { headers: getAuthHeader() });
            setWhatsappStatus({ connected: false });
            toast.success('WhatsApp disconnected');
        } catch {
            toast.error('Failed to disconnect WhatsApp');
        }
    };

    const loadCalendarStatus = async () => {
        setCalendarLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/calendar/status`, { headers: getAuthHeader() });
            setCalendarStatus(res.data);
        } catch (err) {
            console.error('Failed to load Calendar status:', err);
        } finally {
            setCalendarLoading(false);
        }
    };

    const connectCalendar = async () => {
        setConnectingCalendar(true);
        try {
            const res = await axios.post(`${API}/oauth/google/calendar/start`, {}, { headers: getAuthHeader() });
            if (res.data?.data?.auth_url) {
                window.location.href = res.data.data.auth_url;
            } else if (res.data?.auth_url) {
                window.location.href = res.data.auth_url;
            }
        } catch (err) {
            toast.error('Failed to start Calendar connection');
            setConnectingCalendar(false);
        }
    };

    const disconnectCalendar = async () => {
        try {
            await axios.post(`${API}/oauth/calendar/disconnect`, {}, { headers: getAuthHeader() });
            setCalendarStatus({ connected: false });
            toast.success('Calendar disconnected');
        } catch {
            toast.error('Failed to disconnect Calendar');
        }
    };

    const loadApifyStatus = async () => {
        // D4: the stored key is write-only — the page learns the state from the
        // status route and never receives (or pre-fills) the key itself.
        try {
            const res = await axios.get(`${API}/enrichment/apify/status`, { headers: getAuthHeader() });
            setApifyStatus(res.data);
        } catch {}
    };

    const validateApifyKey = async () => {
        if (!apifyKey.trim()) return toast.error('Enter an API key');
        setApifyValidating(true);
        try {
            const res = await axios.post(`${API}/enrichment/apify/validate-key`, { api_key: apifyKey }, { headers: getAuthHeader() });
            if (res.data.valid) {
                await axios.put(`${API}/organizations/current`, { settings: { apify_api_key: apifyKey } }, { headers: getAuthHeader() });
                setApifyStatus({ configured: true, valid: true, username: res.data.username, plan: res.data.plan });
                setApifyKey('');
                toast.success('Apify connected!');
                await loadApifyStatus();
            } else {
                toast.error('Invalid API key');
                setApifyStatus(null);
            }
        } catch (err) { toast.error(err.response?.data?.detail || `Validation failed: ${err.message}`); }
        finally { setApifyValidating(false); }
    };

    const disconnectApify = async () => {
        try {
            await axios.put(`${API}/organizations/current`, { settings: { apify_api_key: null } }, { headers: getAuthHeader() });
            setApifyKey('');
            setApifyStatus(null);
            toast.success('Apify disconnected');
        } catch { toast.error('Failed to disconnect Apify'); }
    };

    const loadExaStatus = async () => {
        // D4: same write-only rule for the Exa key.
        try {
            const res = await axios.get(`${API}/enrichment/exa/status`, { headers: getAuthHeader() });
            setExaStatus(res.data);
        } catch {}
    };

    const validateExaKey = async () => {
        if (!exaKey.trim()) return toast.error('Enter an API key');
        setExaValidating(true);
        try {
            const res = await axios.post(`${API}/enrichment/exa/validate-key`, { api_key: exaKey }, { headers: getAuthHeader() });
            if (res.data.valid) {
                await axios.put(`${API}/organizations/current`, { settings: { exa_api_key: exaKey } }, { headers: getAuthHeader() });
                setExaStatus({ configured: true, valid: true });
                setExaKey('');
                toast.success('Exa connected!');
                await loadExaStatus();
            } else {
                toast.error('Invalid API key');
                setExaStatus(null);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Validation failed'); }
        finally { setExaValidating(false); }
    };

    const disconnectExa = async () => {
        try {
            await axios.put(`${API}/organizations/current`, { settings: { exa_api_key: null } }, { headers: getAuthHeader() });
            setExaKey('');
            setExaStatus(null);
            toast.success('Exa disconnected');
        } catch { toast.error('Failed to disconnect'); }
    };

    return (
        <SettingsLayout title="Integrations" description="Connect your email, calendar, and data enrichment accounts">
            <div className="space-y-4 max-w-lg">
                <p className="text-sm text-stone-500">Connect your email and calendar accounts to automatically log communications.</p>

                {/* Gmail Integration */}
                <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                <Mail className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">Gmail</p>
                                <p className="text-xs text-stone-400">Sync emails and send from your Gmail</p>
                            </div>
                        </div>
                        {gmailLoading ? (
                            <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                        ) : gmailStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : gmailStatus?.needs_reconnect ? (
                            <button
                                onClick={connectGmail}
                                disabled={connectingGmail}
                                className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                                {connectingGmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
                            </button>
                        ) : (
                            <button
                                onClick={connectGmail}
                                disabled={connectingGmail}
                                data-testid="settings-connect-gmail"
                                className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50">
                                {connectingGmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                            </button>
                        )}
                    </div>

                    {gmailStatus?.needs_reconnect && (
                        <div className="mt-4 pt-4 border-t border-amber-100">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs font-medium text-amber-800">Session expired for {gmailStatus.gmail_email || gmailStatus.gmail_name || 'your Gmail'}</p>
                                <p className="text-[11px] text-amber-600 mt-1">Click Reconnect above to re-authorize your Gmail access.</p>
                            </div>
                        </div>
                    )}
                    {gmailStatus?.connected && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="flex items-center gap-3 mb-3">
                                {gmailStatus.gmail_picture && (
                                    <img src={gmailStatus.gmail_picture} alt="" className="w-8 h-8 rounded-full" />
                                )}
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{gmailStatus.gmail_name}</p>
                                    <p className="text-xs text-stone-500">{gmailStatus.gmail_email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate('/inbox?source=gmail')}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-100">
                                    <ExternalLink className="w-3 h-3" /> View Emails
                                </button>
                                <button
                                    onClick={disconnectGmail}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    )}
                    {!gmailStatus?.connected && gmailOrphans > 0 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs font-medium text-amber-800">
                                    {gmailOrphans} Gmail email{gmailOrphans === 1 ? '' : 's'} are still in your inbox
                                </p>
                                <p className="text-[11px] text-amber-600 mt-1">
                                    This mailbox is disconnected, but the messages it synced are still listed. Removing them is permanent.
                                </p>
                                <button
                                    onClick={() => clearOrphanedMail('gmail')}
                                    disabled={clearingOrphans === 'gmail'}
                                    data-testid="settings-clear-gmail-orphans"
                                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50">
                                    {clearingOrphans === 'gmail'
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <X className="w-3 h-3" />} Remove from inbox
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Google Calendar Integration */}
                <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                <CalendarDays className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">Google Calendar</p>
                                <p className="text-xs text-stone-400">Sync meetings and schedule from CRM</p>
                            </div>
                        </div>
                        {calendarLoading ? (
                            <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                        ) : calendarStatus?.connected && calendarStatus?.needs_reconnect ? (
                            <button
                                onClick={connectCalendar}
                                disabled={connectingCalendar}
                                data-testid="settings-reconnect-calendar"
                                className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                                {connectingCalendar ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
                            </button>
                        ) : calendarStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : (
                            <button
                                onClick={connectCalendar}
                                disabled={connectingCalendar}
                                data-testid="settings-connect-calendar"
                                className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50">
                                {connectingCalendar ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                            </button>
                        )}
                    </div>

                    {calendarStatus?.connected && calendarStatus?.needs_reconnect && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="calendar-needs-reconnect">
                            <p className="text-xs font-medium text-amber-800">Calendar access needs to be renewed for {calendarStatus.calendar_email || 'your Google account'}</p>
                            <p className="text-[11px] text-amber-600 mt-1">Click Reconnect above to re-authorize Google Calendar.</p>
                        </div>
                    )}
                    {calendarStatus?.connected && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="flex items-center gap-3 mb-3">
                                {calendarStatus.calendar_picture && (
                                    <img src={calendarStatus.calendar_picture} alt="" className="w-8 h-8 rounded-full" />
                                )}
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{calendarStatus.calendar_name}</p>
                                    <p className="text-xs text-stone-500">{calendarStatus.calendar_email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate('/calendar')}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200">
                                    <CalendarDays className="w-3 h-3" /> View Meetings
                                </button>
                                <button
                                    onClick={disconnectCalendar}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* WhatsApp Business Integration */}
                <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E8FFF0' }}>
                                <MessageCircle className="w-5 h-5" style={{ color: '#25D366' }} />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">WhatsApp Business</p>
                                <p className="text-xs text-stone-400">Send and receive WhatsApp messages from CRM</p>
                            </div>
                        </div>
                        {whatsappLoading ? (
                            <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                        ) : whatsappStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : (
                            <button
                                onClick={connectWhatsApp}
                                disabled={connectingWhatsApp}
                                data-testid="settings-connect-whatsapp"
                                className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-200 transition-colors disabled:opacity-50">
                                {connectingWhatsApp ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                            </button>
                        )}
                    </div>

                    {whatsappStatus?.connected && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8FFF0' }}>
                                    <MessageCircle className="w-4 h-4" style={{ color: '#25D366' }} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{whatsappStatus.display_name}</p>
                                    <p className="text-xs text-stone-500">{whatsappStatus.phone_number}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    whatsappStatus.quality_rating === 'GREEN' ? 'bg-emerald-50 text-emerald-700' :
                                    whatsappStatus.quality_rating === 'YELLOW' ? 'bg-amber-50 text-amber-700' :
                                    'bg-red-50 text-red-700'
                                }`}>
                                    Quality: {whatsappStatus.quality_rating}
                                </span>
                                <span className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">
                                    {whatsappStatus.messaging_tier}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate('/inbox?tab=whatsapp')}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200">
                                    <ExternalLink className="w-3 h-3" /> Open Chat
                                </button>
                                <button
                                    onClick={disconnectWhatsApp}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Apify Data Enrichment */}
                <div data-testid="integration-apify-status" className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                <Database className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">Apify Data Enrichment</p>
                                <p className="text-xs text-stone-400">Connect Apify for LinkedIn scraping and web enrichment</p>
                            </div>
                        </div>
                        {apifyStatus?.configured && apifyStatus?.valid !== false ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : null}
                    </div>

                    {apifyStatus?.configured && apifyStatus?.valid !== false ? (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            {apifyStatus?.valid === null && (
                                <p data-testid="integration-apify-check-unavailable" className="mb-3 text-xs text-stone-500">
                                    Couldn't check the key right now.
                                </p>
                            )}
                            <div className="flex items-center gap-3 mb-3">
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{apifyStatus.username || 'Apify User'}</p>
                                    <p className="text-xs text-stone-500">Plan: {apifyStatus.plan || 'Unknown'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={disconnectApify}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                            {apifyStatus?.configured && apifyStatus?.valid === false && (
                                <div data-testid="integration-apify-unverified" className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                                    A saved key could not be verified. Enter a new key or disconnect.
                                </div>
                            )}
                            <div className="relative">
                                <input
                                    type={showApifyKey ? 'text' : 'password'}
                                    value={apifyKey}
                                    onChange={e => setApifyKey(e.target.value)}
                                    placeholder="Enter your Apify API key"
                                    className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowApifyKey(!showApifyKey)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                    {showApifyKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={validateApifyKey}
                                    disabled={apifyValidating}
                                    className="px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                                    {apifyValidating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                                    {apifyValidating ? 'Validating...' : 'Validate & Connect'}
                                </button>
                                <a href="https://apify.com" target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                    Don't have an account? Sign up at apify.com <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                            {apifyStatus?.configured && apifyStatus?.valid === false && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={disconnectApify}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                        <X className="w-3 h-3" /> Disconnect
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Exa AI Search */}
                <div data-testid="integration-exa-status" className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                                <Search className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">Exa AI Search</p>
                                <p className="text-xs text-stone-400">Connect Exa for AI-powered company & contact enrichment</p>
                            </div>
                        </div>
                        {exaStatus?.configured && exaStatus?.valid !== false ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : null}
                    </div>

                    {exaStatus?.configured && exaStatus?.valid !== false ? (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            {exaStatus?.valid === null && (
                                <p data-testid="integration-exa-check-unavailable" className="mb-3 text-xs text-stone-500">
                                    Couldn't check the key right now.
                                </p>
                            )}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={disconnectExa}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                            {exaStatus?.configured && exaStatus?.valid === false && (
                                <div data-testid="integration-exa-unverified" className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
                                    A saved key could not be verified. Enter a new key or disconnect.
                                </div>
                            )}
                            <div className="relative">
                                <input
                                    type={showExaKey ? 'text' : 'password'}
                                    value={exaKey}
                                    onChange={e => setExaKey(e.target.value)}
                                    placeholder="Enter your Exa API key"
                                    className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowExaKey(!showExaKey)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                    {showExaKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={validateExaKey}
                                    disabled={exaValidating}
                                    className="px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                                    {exaValidating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                                    {exaValidating ? 'Validating...' : 'Validate & Connect'}
                                </button>
                                <a href="https://exa.ai" target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                    Don't have an account? Sign up at exa.ai <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                            {exaStatus?.configured && exaStatus?.valid === false && (
                                <div className="flex justify-end">
                                    <button
                                        onClick={disconnectExa}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                        <X className="w-3 h-3" /> Disconnect
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Outlook Integration */}
                <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                <Mail className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-stone-900">Microsoft Outlook</p>
                                <p className="text-xs text-stone-400">
                                    {outlookStatus?.connected ? outlookStatus.outlook_email : 'Connect Office 365 email'}
                                </p>
                            </div>
                        </div>
                        {outlookLoading ? (
                            <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                        ) : outlookStatus?.connected ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <CheckCircle className="w-3 h-3" /> Connected
                            </span>
                        ) : outlookStatus?.needs_reconnect ? (
                            <button
                                onClick={connectOutlook}
                                disabled={connectingOutlook}
                                className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                                {connectingOutlook ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
                            </button>
                        ) : (
                            <button
                                onClick={connectOutlook}
                                disabled={connectingOutlook}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                            >
                                {connectingOutlook ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                            </button>
                        )}
                    </div>

                    {outlookStatus?.needs_reconnect && (
                        <div className="mt-4 pt-4 border-t border-amber-100">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs font-medium text-amber-800">Session expired for {outlookStatus.outlook_email || 'your Outlook account'}</p>
                                <p className="text-[11px] text-amber-600 mt-1">Click Reconnect above to re-authorize your Outlook access.</p>
                            </div>
                        </div>
                    )}

                    {outlookStatus?.connected && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="flex items-center gap-3 mb-3">
                                <div>
                                    <p className="text-sm font-medium text-stone-900">{outlookStatus.outlook_email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={disconnectOutlook} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                    <X className="w-3 h-3" /> Disconnect
                                </button>
                            </div>
                        </div>
                    )}

                    {!outlookStatus?.connected && outlookOrphans > 0 && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs font-medium text-amber-800">
                                    {outlookOrphans} Outlook email{outlookOrphans === 1 ? '' : 's'} are still in your inbox
                                </p>
                                <p className="text-[11px] text-amber-600 mt-1">
                                    This mailbox is disconnected, but the messages it synced are still listed. Removing them is permanent.
                                </p>
                                <button
                                    onClick={() => clearOrphanedMail('outlook')}
                                    disabled={clearingOrphans === 'outlook'}
                                    data-testid="settings-clear-outlook-orphans"
                                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50">
                                    {clearingOrphans === 'outlook'
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <X className="w-3 h-3" />} Remove from inbox
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* CommandRoom Integration */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors opacity-60">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                            <Link className="w-5 h-5 text-violet-500" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-stone-900">CommandRoom.ai</p>
                            <p className="text-xs text-stone-400">Sync account data and org charts</p>
                        </div>
                    </div>
                    <span className="px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-500 rounded-full">
                        Coming Soon
                    </span>
                </div>
            </div>
        </SettingsLayout>
    );
}
